<script setup lang="ts">
import {computed, reactive, ref} from "vue";
import {Avatar, Badge, Button, Dialog, Icon, Menu, Message} from "@/Components/Library";
import {RouterLink} from "vue-router";
import {GroupName} from "@/Components/Global";
import {resendInvitation, withdrawInvitation} from "@/Services/invitations.ts";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import {GroupRole} from "@/Enums";
import AddContactDrawer from "@/Pages/User/Contacts/AddContactDrawer.vue";
import GroupMemberDrawer from "@/Pages/User/Groups/GroupMemberDrawer.vue";
import {changeGroupMemberRole, removeGroupMember} from "@/Services/group-members.ts";
import {transferGroupOwnership} from "@/Services/groups.ts";
import useAnswerProgress from "@/Composables/answerProgress.ts";

const toast = useToast();
const confirm = useConfirm();
const {getTotalCount, getAnsweredCount, getTrafficBadgeClass} = useAnswerProgress();
const props = defineProps<{
    group: any;
}>();
const state = reactive({
    show: {
        members: true,
        memberMenu: false,
        memberStats: false,
        pendingInvitations: false,
        addContactDialog: false,
    },
});

const selectedMember = ref();
const sortedMembers = computed(() => {
    const members = props.group?.members?.filter((member) => member.id !== props.group.user.id) ?? [];

    return {
        admins: members.filter((member) => member.pivot.role !== GroupRole.member.value),
        members: members.filter((member) => member.pivot.role === GroupRole.member.value),
    };
});

const totalQuestionsCount = computed(() => getTotalCount(props.group?.questions_count));

const handleResendInvitation = (invitation) => {
    confirm.require({
        header: `Resend invitation`,
        message: `Are you sure you want to resend this invitation?`,
        acceptLabel: "Yes",
        rejectLabel: "No",
        acceptClass: "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
        rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
        accept: async () => {
            await resendInvitation(invitation.id);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "Invitation has been resent.",
                life: 3000,
            });
        },
    });
};

const handleWithdrawInvitation = (invitation) => {
    confirm.require({
        header: `Withdraw invitation`,
        message: `Are you sure you want to withdraw this invitation?`,
        acceptLabel: "Yes",
        rejectLabel: "No",
        acceptClass: "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
        rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
        accept: async () => {
            await withdrawInvitation(invitation.id);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "Invitation has been withdrawn.",
                life: 3000,
            });
            //TODO check if this is ok, or should just update store directly. Because this is a computed property with a setter, does mutating prop directly update parent ref safely?
            props.group.invitations = props.group.invitations.filter((i) => i.id !== invitation.id);
        },
    });
};

const showMemberDrawer = (member) => {
    selectedMember.value = member;
    console.log(member);
    state.show.memberStats = true;
};

const memberMenuItems = ref([
    {
        label: selectedMember.value?.name,
        items: [],
    },
]);

const changeRole = (role: string, member: any) =>
    confirm.require({
        header: "Change role",
        message: `Are you sure you want to make ${member.first_name} ${member.last_name} ${role === "admin" ? "an " : "a "}${GroupRole[role].name}?`,
        acceptClass: "bg-askr-green px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: async () => {
            await changeGroupMemberRole(props.group.id, member.id, {role: role});
            props.group.members = props.group.members.map((m) => {
                if (m.id === member.id) {
                    m.pivot.role = GroupRole[role].value;
                }
                return m;
            });
            toast.add({
                severity: "success",
                summary: "Role changed",
                detail: `${member.first_name} ${member.last_name} is now ${role === "admin" ? "an " : "a "}${GroupRole[role].name}.`,
                life: 3000,
            });
        },
    });

const memberMenuItemsBase = [
    {
        label: "Remove from group",
        icon: "prime:user-minus",
        command: () =>
            confirm.require({
                header: "Remove member",
                message: `Are you sure you want to remove ${selectedMember.value.first_name} ${selectedMember.value.last_name} from this group?`,
                acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
                rejectClass: "px-3 py-1.5",
                accept: async () => {
                    await removeGroupMember(props.group.id, selectedMember.value.id);
                    toast.add({
                        severity: "success",
                        summary: "Member removed",
                        detail: `${selectedMember.value.first_name} ${selectedMember.value.last_name} has been removed from the group.`,
                        life: 3000,
                    });
                    props.group.members = props.group.members.filter((member) => member.id !== selectedMember.value.id);
                    state.show.memberMenu = false;
                },
            }),
        class: "text-red-600 hover:bg-red-100",
    },
];

const makeAdminMenuItem = {
    label: "Promote to admin",
    icon: "prime:shield",
    command: () => {
        changeRole("admin", selectedMember.value);
        state.show.memberMenu = false;
    },
};

const makeCreatorMenuItem = {
    label: "Promote to creator",
    icon: "prime:pencil",
    command: () => {
        changeRole("creator", selectedMember.value);
        state.show.memberMenu = false;
    },
};

const makeMemberMenuItem = {
    label: "Demote to member",
    icon: "prime:user",
    command: () => {
        changeRole("member", selectedMember.value);
        state.show.memberMenu = false;
    },
};

const transferOwnershipMenuItem = {
    label: "Make group owner",
    icon: "prime:user",
    command: () =>
        confirm.require({
            message: `Are you sure you want to make ${selectedMember.value.first_name} the new owner of this group?`,
            header: "Make group owner",
            acceptLabel: "Make owner",
            rejectLabel: "Cancel",
            acceptClass: "bg-red-600 text-white px-3 py-1.5 rounded-md confirm-delete-yes hover:brightness-110",
            rejectClass: "px-3 py-1.5 confirm-delete-no",
            accept: async () => {
                transferGroupOwnership(props.group.id, selectedMember.value.id).then(async () => {
                    toast.add({
                        severity: "success",
                        summary: "Ownership transferred",
                        detail: `${selectedMember.value.first_name} is now the owner of the group.`,
                        life: 3000,
                    });
                    state.show.memberMenu = false;
                    await loadGroup();
                });
            },
            reject: () => {},
        }),
};

const toggleMemberMenu = (member) => {
    const newItems = [];
    if (props.group?.is_owner) {
        newItems.push(transferOwnershipMenuItem);
        if (member.pivot.role !== GroupRole.admin.value) {
            newItems.push(makeAdminMenuItem);
        }
        if (member.pivot.role !== GroupRole.member.value) {
            newItems.push(makeMemberMenuItem);
        }
        if (member.pivot.role !== GroupRole.creator.value) {
            newItems.push(makeCreatorMenuItem);
        }
    }

    selectedMember.value = member;
    memberMenuItems.value[0].label = selectedMember.value?.first_name + " " + selectedMember.value?.last_name;
    memberMenuItems.value[0].items = [...newItems, ...memberMenuItemsBase];
    state.show.memberMenu = true;
};
</script>
<template>
    <div class="overflow-hidden">
        <div class="flex items-end">
            <div class="relative pr-8">
                <h3 class="mb-2 font-bold">People</h3>
                <Badge :value="group.members.length" class="absolute right-0 top-0 bg-askr-green text-white" />
            </div>
            <div v-if="group.is_owner || group.can_admin" class="ml-auto">
                <RouterLink :to="{name: 'groups.edit.members', params: {group: group.id}}">
                    <Button
                        :icon="{icon: 'material-symbols:manage-search-rounded', width: 26}"
                        rounded
                        class="h-10 w-10 border bg-stone-50 shadow-sm shadow-stone-300 dark:border-stone-800 dark:bg-stone-900 dark:shadow-stone-700"
                    />
                </RouterLink>
            </div>
        </div>
        <div class="mb-4 flex flex-col border-b pb-4 dark:border-stone-600">
            <button
                type="button"
                class="flex items-center gap-2 py-2 hover:bg-stone-50 dark:hover:bg-stone-900"
                @click="showMemberDrawer(group.user)"
            >
                <Avatar class="h-8 w-8 flex-none shrink-0 text-xs" :user="group.user" shape="circle" />
                <div>
                    <span class="font-medium" v-if="group.user.first_name">
                        {{ group.user.first_name }}
                    </span>
                    <span class="ml-1 font-bold" v-if="group.user.last_name">
                        {{ group.user.last_name }}
                    </span>
                </div>
                <span class="flex items-center gap-1 rounded bg-askr-green p-1 text-xs font-semibold text-white">
                    <Icon icon="mdi:shield-account" width="14" />
                    OWNER
                </span>
            </button>
        </div>
        <div v-auto-animate>
            <div v-if="group.members && state.show.members" class="flex flex-col">
                <template v-for="(member, i) in sortedMembers.admins" :key="member.id">
                    <button
                        v-if="member.id !== group.user.id"
                        type="button"
                        class="flex items-center gap-2 py-2 hover:bg-stone-50 dark:hover:bg-stone-900"
                    >
                        <span class="flex grow items-center gap-2">
                            <GroupName
                                :anonymization="group.anonymization.slug"
                                :member="member"
                                :i="i"
                                :is-owner="group.is_owner"
                            />
                        </span>
                        <div class="ml-auto flex items-center gap-2">
                            <Icon
                                v-if="member.pivot.role === GroupRole.admin.value"
                                icon="la:user-tie"
                                width="20"
                                class="text-stone-500"
                            />
                            <Icon
                                v-if="member.pivot.role === GroupRole.creator.value"
                                icon="mingcute:user-edit-line"
                                width="20"
                                class="text-stone-500"
                            />
                            <Button
                                class="!ring-0"
                                v-if="(group.is_owner || group.is_admin) && member.id !== group.user.id"
                                :icon="{
                                    icon: 'pepicons-pencil:dots-y',
                                    width: '24',
                                }"
                                @click.stop="toggleMemberMenu(member)"
                            />
                        </div>
                    </button>
                </template>
                <template v-for="(member, i) in sortedMembers.members" :key="member.id">
                    <button
                        type="button"
                        class="flex items-center gap-2 py-2 hover:bg-stone-50 dark:hover:bg-stone-900"
                    >
                        <span class="flex grow items-center gap-2">
                            <GroupName
                                :anonymization="group.anonymization.slug"
                                :member="member"
                                :i="i"
                                :is-owner="group.is_owner"
                            />
                        </span>
                        <div class="ml-auto flex items-center gap-2">
                            <span
                                class="flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold"
                                :class="getTrafficBadgeClass(member.answered_count, totalQuestionsCount)"
                            >
                                {{ getAnsweredCount(member.answered_count, totalQuestionsCount) }}
                            </span>
                            <Icon
                                v-if="member.pivot.role === GroupRole.admin.value"
                                icon="la:user-tie"
                                width="20"
                                class="text-stone-500"
                            />
                            <Icon
                                v-if="member.pivot.role === GroupRole.creator.value"
                                icon="mingcute:user-edit-line"
                                width="20"
                                class="text-stone-500"
                            />
                            <Button
                                class="!ring-0"
                                v-if="(group.is_owner || group.is_admin) && member.id !== group.user.id"
                                :icon="{
                                    icon: 'pepicons-pencil:dots-y',
                                    width: '24',
                                }"
                                @click.stop="toggleMemberMenu(member)"
                            />
                        </div>
                    </button>
                </template>
                <div v-if="!group?.members?.length" class="py-4 text-center">No members yet.</div>
                <div v-if="group.invitations?.length" v-auto-animate>
                    <div v-if="state.show.pendingInvitations">
                        <h3 class="mb-2 mt-6 text-sm font-bold">Pending invitations</h3>
                        <div class="flex flex-col gap-1" v-auto-animate>
                            <template v-for="invitation in group.invitations" :key="invitation.id">
                                <Message
                                    severity="warn"
                                    class="dark:text-white"
                                    :pt:text="`flex items-center justify-between w-full text-base leading-none font-medium`"
                                >
                                    <div class="flex w-full grow items-center justify-between">
                                        <span class="font-medium">{{ invitation.first_name }}</span>
                                        <span>&nbsp;</span>
                                        <span class="font-bold">{{ invitation.last_name }}</span>
                                        <Button
                                            :icon="{
                                                icon: 'mdi:refresh',
                                                width: 16,
                                            }"
                                            class="ml-auto !gap-1 !p-0 !px-2 text-xs font-semibold"
                                            @click="handleResendInvitation(invitation)"
                                            text
                                        >
                                            Resend
                                        </Button>
                                        <Button
                                            :icon="{
                                                icon: 'mdi:trash-outline',
                                            }"
                                            class="ml-2"
                                            text
                                            @click="handleWithdrawInvitation(invitation)"
                                        />
                                    </div>
                                </Message>
                            </template>
                        </div>
                    </div>
                    <Button
                        @click="state.show.pendingInvitations = !state.show.pendingInvitations"
                        :icon="state.show.pendingInvitations ? 'mdi:chevron-up' : 'mdi:chevron-down'"
                        text
                        class="mt-3 !gap-1 whitespace-nowrap text-xs font-normal text-stone-500"
                    >
                        {{ state.show.pendingInvitations ? "Hide" : "Show" }} pending invitations
                    </Button>
                </div>
            </div>
        </div>
        <div class="mt-0 flex w-full justify-end">
            <button
                type="button"
                class="flex items-center gap-1 text-xs"
                @click="state.show.members = !state.show.members"
            >
                <Icon :icon="state.show.members ? 'akar-icons:chevron-up' : 'akar-icons:chevron-down'" />
                {{ state.show.members ? "Hide" : "Show" }} members
            </button>
        </div>
        <Dialog
            v-model:visible="state.show.memberMenu"
            modal
            dismissable-mask
            class="max-w-md overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <Menu :model="memberMenuItems" />
            </template>
        </Dialog>
        <AddContactDrawer v-model:visible="state.show.addContactDialog" :group="group" @invited="" />
        <GroupMemberDrawer v-model="state.show.memberStats" :member="selectedMember" :group="group" />
    </div>
</template>
<style scoped></style>
